<script lang="ts">
  import { getRoutes } from '../../lib/api';
  import type { Endpoint } from '../../lib/endpoint';
  import { withUniqueKeys } from '../../lib/unique-keys';
  import { journeySignature } from '../../lib/journey-key';
  import { buildRouteQuery } from '../../lib/route-query';
  import type { EndpointsStore } from '../../stores/endpoints.svelte';
  import type { RouteResponseStore } from '../../stores/route-response.svelte';
  import type { TimeStore } from '../../stores/time.svelte';
  import type { StopJson } from '../../types/StopJson';
  import ClipboardButton from '../clipboard-button.svelte';
  import EmptyState from '../empty-state.svelte';
  import EndpointInput from '../endpoint-input.svelte';
  import FreshnessChip from '../freshness-chip.svelte';
  import JourneyCard from '../journey-card.svelte';
  import SwapButton from '../swap-button.svelte';
  import TimePicker from '../time-picker.svelte';
  import ExcludeWidget from './exclude-widget.svelte';
  import JourneyDetail from './journey-detail.svelte';
  import type { ExcludeStore } from '../../stores/exclude.svelte';
  import { nowHHMM } from '../../lib/format';

  interface Properties {
    endpoints: EndpointsStore;
    time: TimeStore;
    route: RouteResponseStore;
    exclude: ExcludeStore;
    /** Optional hook fired after a successful search — parent uses it for URL sync + last-route cache. */
    onAfterSearch?: () => void;
    /**
     * Fires after any chip mutation; typically for URL sync. Do NOT persist
     * `route.journeys` under the new signature — they still reflect the
     * pre-change chips and would poison the last-route cache.
     */
    onChipChange?: () => void;
    /**
     * Fires after swap exchanges endpoints; parent uses it for URL sync.
     * Do NOT persist `route.journeys` — they're still in pre-swap direction
     * and would poison the last-route cache under the new signature.
     */
    onAfterSwap?: () => void;
    /** Fires when the user clicks a stop name in a journey timeline — parent
     *  should switch to the Departures tab and load that stop's board. */
    onOpenStopDepartures?: (stop: StopJson) => void;
    /** Arms the app-level map picker to fill origin on the next map click. */
    onPickOriginFromMap?: () => void;
    /** Arms the app-level map picker to fill destination on the next map click. */
    onPickDestFromMap?: () => void;
    /** Which endpoint the picker is currently armed for, if any — drives the button highlight. */
    mapPickerTarget?: 'origin' | 'destination';
  }

  const {
    endpoints,
    time,
    route,
    exclude,
    onAfterSearch,
    onChipChange,
    onAfterSwap,
    onOpenStopDepartures,
    onPickOriginFromMap,
    onPickDestFromMap,
    mapPickerTarget,
  }: Properties = $props();

  async function search(): Promise<void> {
    /* istanbul ignore if -- defensive: the Search button is disabled unless both endpoints are set */
    if (!endpoints.origin || !endpoints.dest) return;
    // Materialize 'now' into the current wall-clock so the URL/params carry
    // the exact minute the user saw — otherwise the server picks its own now
    // and the response can drift by a minute or two.
    const wireTime = time.mode === 'now' ? nowHHMM() : time.toUrlValue();
    const block = exclude.toBlockParams();
    const query = buildRouteQuery(endpoints.origin, endpoints.dest, {
      time: wireTime,
      blockLines: block.lines || null,
      blockModes: block.modes || null,
    });
    const isApplied = await route.load((signal) => getRoutes(query, signal));
    if (isApplied) onAfterSearch?.();
  }

  const isSearching = $derived(route.state === 'loading');

  function currentUrl(): string {
    return location.href;
  }

  /**
   * Journeys paired with list-unique `{#each}` keys; two journeys can share a
   * `journeySignature`, and a keyed block requires distinct keys.
   */
  const keyedJourneys = $derived(withUniqueKeys(route.journeys, journeySignature));
</script>

<!-- Origin/dest inputs → search → results list. The parent owns the stores,
     so URL restore, label hydration, and last-route recall land in a single place. -->
<div class="route-tab">
  <EndpointInput
    label="From"
    scope="route"
    value={endpoints.originLabel}
    disabled={isSearching}
    onInput={(value: string) => {
      endpoints.originLabel = value;
    }}
    onPick={(endpoint: Endpoint, label: string) => {
      endpoints.setOrigin(endpoint, label);
    }}
    onPickFromMap={onPickOriginFromMap}
    pickerArmed={mapPickerTarget === 'origin'}
  />
  <SwapButton
    onSwap={() => {
      endpoints.swap();
      onAfterSwap?.();
    }}
    disabled={isSearching || (!endpoints.origin && !endpoints.dest)}
  />
  <EndpointInput
    label="To"
    scope="route"
    value={endpoints.destLabel}
    disabled={isSearching}
    onInput={(value: string) => {
      endpoints.destLabel = value;
    }}
    onPick={(endpoint: Endpoint, label: string) => {
      endpoints.setDest(endpoint, label);
    }}
    onPickFromMap={onPickDestFromMap}
    pickerArmed={mapPickerTarget === 'destination'}
  />
  <TimePicker store={time} />
  <ExcludeWidget store={exclude} onChange={onChipChange ?? onAfterSearch} />
  <button
    type="button"
    class="search-btn"
    disabled={!endpoints.isReady || isSearching}
    onclick={() => {
      void search();
    }}
  >
    {isSearching ? 'Searching…' : 'Search'}
  </button>

  {#if route.state === 'error' && route.errorMessage}
    <p class="error">{route.errorMessage}</p>
  {/if}

  {#if route.state === 'ready'}
    {#if route.freshness}<FreshnessChip freshness={route.freshness} />{/if}
    {#if route.journeys.length === 0}
      <EmptyState message="No journeys found." />
    {:else}
      <div class="share-row">
        <ClipboardButton label="Copy link" copiedLabel="Copied!" getText={currentUrl} />
      </div>
      <div class="journey-list">
        {#each keyedJourneys as entry, index (entry.key)}
          <JourneyCard
            journey={entry.item}
            index={index + 1}
            active={route.selectedIndex === index}
            onSelect={() => {
              route.select(index);
            }}
          />
          {#if route.selectedIndex === index}
            {#key entry.key}
              <JourneyDetail journey={entry.item} {onOpenStopDepartures} />
            {/key}
          {/if}
        {/each}
      </div>
    {/if}
  {/if}
</div>

<style>
  .route-tab {
    display: flex;
    flex-direction: column;
  }

  .search-btn {
    background: var(--color-accent-green);
    border: none;
    color: #000;
    padding: 8px 12px;
    border-radius: var(--radius-sm);
    cursor: pointer;
    font-weight: 600;
    margin-bottom: var(--space-md);
  }

  .search-btn:hover:not(:disabled) {
    background: var(--color-success);
    color: #fff;
  }

  .search-btn:disabled {
    background: var(--color-surface);
    color: var(--color-fg-muted);
    opacity: 0.6;
    cursor: not-allowed;
  }

  .error {
    color: var(--color-accent-red);
    font-size: var(--font-size-sm);
    margin-bottom: var(--space-md);
  }

  .journey-list {
    display: flex;
    flex-direction: column;
  }

  .share-row {
    display: flex;
    justify-content: flex-end;
    margin-bottom: var(--space-sm);
  }
</style>
